import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { SelectButton } from "primereact/selectbutton";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { apiService } from "../../../api/api";
import { WidgetFilterValues } from "../prefs/homePrefs";
import {
  MANAGERS_KEY,
  SCOPE_KEY,
  SelectOption,
  WidgetDefinition,
  getScope,
  getStringArray,
} from "./types";

let managerOptionsPromise: Promise<SelectOption[]> | null = null;

/** Channel managers from channelSettings (cached for the session). */
export const loadManagerOptions = (): Promise<SelectOption[]> => {
  if (!managerOptionsPromise) {
    managerOptionsPromise = apiService.channelSettings
      .getAllChannelSettings()
      .then((channels) => {
        const byEmail = new Map<string, string>();
        channels.forEach((c: any) => {
          const emails: string[] = Array.isArray(c.ChannelManagerEmail) ? c.ChannelManagerEmail : [];
          const names: string[] = Array.isArray(c.ChannelManager) ? c.ChannelManager : [];
          emails.forEach((email, i) => {
            const key = String(email).trim().toLowerCase();
            if (!key || byEmail.has(key)) return;
            byEmail.set(key, names[i] ? `${names[i]} (${key})` : key);
          });
        });
        return Array.from(byEmail.entries())
          .map(([value, label]) => ({ value, label }))
          .sort((a, b) => a.label.localeCompare(b.label));
      })
      .catch((e) => {
        managerOptionsPromise = null;
        throw e;
      });
  }
  return managerOptionsPromise;
};

interface Props {
  visible: boolean;
  def: WidgetDefinition;
  filters: WidgetFilterValues;
  dynamicOptions: Record<string, SelectOption[]>;
  mineLabel: string;
  onHide: () => void;
  onSave: (values: WidgetFilterValues) => void;
  onReset: () => void;
}

const WidgetFilterDialog: React.FC<Props> = ({
  visible,
  def,
  filters,
  dynamicOptions,
  mineLabel,
  onHide,
  onSave,
  onReset,
}) => {
  const [draft, setDraft] = useState<WidgetFilterValues>(filters);
  const [managerOptions, setManagerOptions] = useState<SelectOption[]>([]);
  const [managersLoading, setManagersLoading] = useState(false);

  useEffect(() => {
    if (visible) setDraft(filters);
  }, [visible, filters]);

  const scope = getScope(draft);

  useEffect(() => {
    if (!visible || scope !== "managers" || managerOptions.length) return;
    setManagersLoading(true);
    loadManagerOptions()
      .then(setManagerOptions)
      .catch((e) => console.error("Failed to load channel managers", e))
      .finally(() => setManagersLoading(false));
  }, [visible, scope, managerOptions.length]);

  const set = (key: string, value: unknown) => setDraft((d) => ({ ...d, [key]: value }));

  const footer = (
    <div className="flex justify-content-between align-items-center">
      <Button label="Reset to default" icon="pi pi-refresh" text size="small" onClick={onReset} />
      <div className="flex gap-2">
        <Button label="Cancel" text size="small" onClick={onHide} />
        <Button label="Apply" icon="pi pi-check" size="small" onClick={() => onSave(draft)} />
      </div>
    </div>
  );

  return (
    <Dialog
      header={`Filters · ${def.title}`}
      visible={visible}
      onHide={onHide}
      footer={footer}
      style={{ width: "min(32rem, 94vw)" }}
      dismissableMask
      resizable={false}
      draggable={false}
      className="home-filter-dialog"
    >
      <div className="home-filter-form">
        {def.supportsScope && (
          <div className="home-filter-field">
            <label>Show</label>
            <SelectButton
              value={scope}
              onChange={(e) => e.value && set(SCOPE_KEY, e.value)}
              options={[
                { label: mineLabel, value: "mine" },
                { label: "Everyone", value: "all" },
                { label: "Chosen managers", value: "managers" },
              ]}
            />
            {scope === "managers" && (
              <MultiSelect
                className="w-full mt-2"
                value={getStringArray(draft, MANAGERS_KEY)}
                options={managerOptions}
                onChange={(e) => set(MANAGERS_KEY, e.value)}
                filter
                display="chip"
                placeholder={managersLoading ? "Loading channel managers…" : "Select channel managers"}
                maxSelectedLabels={4}
              />
            )}
          </div>
        )}

        {def.filterFields.map((field) => {
          if (field.type === "toggle") {
            return (
              <div key={field.key} className="home-filter-field home-filter-field--inline">
                <div>
                  <label htmlFor={`f-${def.id}-${field.key}`}>{field.label}</label>
                  {field.help && <small className="home-muted d-block">{field.help}</small>}
                </div>
                <InputSwitch
                  inputId={`f-${def.id}-${field.key}`}
                  checked={draft[field.key] === true}
                  onChange={(e) => set(field.key, !!e.value)}
                />
              </div>
            );
          }
          if (field.type === "select") {
            return (
              <div key={field.key} className="home-filter-field">
                <label>{field.label}</label>
                <Dropdown
                  className="w-full"
                  value={draft[field.key]}
                  options={field.options}
                  onChange={(e) => set(field.key, e.value)}
                />
              </div>
            );
          }
          const options = field.options ?? dynamicOptions[field.key] ?? [];
          return (
            <div key={field.key} className="home-filter-field">
              <label>{field.label}</label>
              <MultiSelect
                className="w-full"
                value={getStringArray(draft, field.key)}
                options={options}
                onChange={(e) => set(field.key, e.value)}
                display="chip"
                filter={options.length > 8}
                placeholder={field.placeholder ?? "Any"}
                maxSelectedLabels={4}
              />
            </div>
          );
        })}
      </div>
    </Dialog>
  );
};

export default WidgetFilterDialog;
